import { Button, ButtonLink } from "@bidbattles/bloks/components/ui/button";
import { Divider } from "@bidbattles/bloks/components/ui/divider";
import { BBBackendQueryOptions } from "@bidbattles/bloks/lib/rapace";
import { useBattleTimer, useLiveBattle } from "@/lib/backend/battle";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@bidbattles/bloks/components/ui/accordion";
import { useUser } from "@/hooks/user";
import { cn } from "@bidbattles/bloks/lib/ui";
import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";
import { LucideLoader2 } from "lucide-react";

export const Route = createFileRoute("/battle/$id/battle")({
	component: RouteComponent,
	ssr: false,
});

function RouteComponent() {
	const queryClient = useQueryClient();
	const { id } = Route.useParams();
	const { data: battle } = useSuspenseQuery(
		BBBackendQueryOptions.fetchBattle({ id }),
	);
	const navigate = Route.useNavigate();
	const liveBattle = useLiveBattle(id);
	const timer = useBattleTimer(id);
	const probationTimer =
		liveBattle.state === "probation" ? liveBattle.remaining : null;
	const bids = [...liveBattle.me.bids.entries()].sort(
		([numberA], [numberB]) => numberA - numberB,
	);
	const user = useUser();
	const toasted = useRef(false);
	const latestBid = liveBattle.bids[0];
	// TODO: Change to use ids
	const isLatestBidder = !!latestBid && latestBid?.name === user?.full_name;

	useEffect(() => {
		(async () => {
			if (
				battle.status === "FINISHED" ||
				battle.status === "STOPPED" ||
				liveBattle.state === "evicted" ||
				liveBattle.state === "completed" ||
				liveBattle.state === "cancelled"
			) {
				if (!toasted.current) {
					if (liveBattle.state === "evicted") {
						toast.error("You have been evicted from the battle.");
					} else if (liveBattle.state === "cancelled") {
						toast.warning("The battle was cancelled.");
					} else {
						toast.info("The battle is over!");
					}
				}
				toasted.current = true;

				await queryClient
					.invalidateQueries(BBBackendQueryOptions.fetchBattle({ id }))
					.catch((error) => console.warn(error));
				navigate({
					from: Route.fullPath,
					to: "/battle/$id/details",
					params: { id },
				});
			}
		})();
	}, [
		battle.status,
		id,
		navigate,
		liveBattle.state,
		queryClient.invalidateQueries,
	]);

	if (liveBattle.state === "connecting") {
		return (
			<div className="p-box flex flex-col justify-center items-center gap-8 col-span-5 w-full">
				<LucideLoader2 className="animate-spin" />
			</div>
		);
	}

	return (
		<motion.div
			layout
			className="p-box flex flex-col justify-start gap-8 col-span-5"
		>
			<AnimatePresence>
				<motion.div layout className="overflow-hidden">
					<motion.p layout className="text-xs text-gray-500 leading-[1.125rem]">
						Battle time
					</motion.p>
					<Timer
						kind="battle"
						value={timer}
						variant={probationTimer ? "sm" : undefined}
					/>
				</motion.div>
				{!!probationTimer && (
					<motion.div>
						<motion.p
							layout
							className="text-xs text-gray-500 leading-[1.125rem]"
							initial={{ translateY: "25%", opacity: 0 }}
							animate={{ translateY: 0, opacity: 1 }}
						>
							Probation
						</motion.p>
						<Timer
							kind="probation"
							className="text-red-400"
							value={probationTimer}
						/>
					</motion.div>
				)}
			</AnimatePresence>
			<Divider render={(props) => <motion.hr layout {...props} />} />
			<motion.div layout className="flex justify-between items-center">
				<p className="text-xs text-gray-500 leading-[1.125rem]">Bids </p>
				{liveBattle.state !== "probation" && (
					<ButtonLink
						variant="outline"
						to="/battle/$id/bids"
						params={{ id }}
						search={{ rebuy: true }}
						size="sm"
					>
						Buy Bids
					</ButtonLink>
				)}
			</motion.div>
			{liveBattle.state === "probation" ? (
				<motion.div
					layout
					className="flex flex-col justify-center items-stretch text-center gap-2 px-box"
				>
					<ButtonLink
						to="/battle/$id/bids"
						params={{ id }}
						search={{ rebuy: true }}
					>
						Buy more bids!
					</ButtonLink>
					<p className="text-gray-500 text-sm">
						Buy more bids befor the time runs out to stay in the battle!
					</p>
				</motion.div>
			) : (
				<motion.div
					layout
					className="flex flex-wrap justify-between lg:justify-around items-center gap-2 lg:gap-4 lg:px-box"
				>
					{bids.map(([number, state]) => {
						return (
							<Button
								key={number}
								size="sm"
								onClick={() => {
									liveBattle.placeBid(number);
								}}
								disabled={!state.balance || isLatestBidder}
								className="grow lg:grow-0"
							>
								<span className="rounded-full px-2 py-1 bg-black/30 text-xs">
									{state.balance}
								</span>
								<span>
									Bid +{number}
									<span className="opacity-90">s</span>
								</span>
							</Button>
						);
					})}
				</motion.div>
			)}
			<Accordion defaultValue={["battle"]}>
				<AccordionItem
					value="battle"
					key={liveBattle.bids
						.map(
							(bid) =>
								`${bid.name}-${bid.amount}-${bid.timestamp?.toISOString()}`,
						)
						.join(" ")}
				>
					<AccordionTrigger>
						<span>
							Battle&nbsp;
							<span className="font-bold">{liveBattle.bids.length}</span>
						</span>
					</AccordionTrigger>
					<AccordionContent className="text-gray-800">
						{liveBattle.bids.length === 0 && <p>No bids made</p>}
						<ol className="list-none flex flex-col gap-4 h-full max-h-[25rem] overflow-y-auto">
							{liveBattle.bids.map((bid) => (
								<li
									key={`${bid.name}-${bid.amount}-${bid.timestamp?.toISOString()}`}
									className={cn(
										"flex gap-2 justify-between",
										bid.name === `${user?.first_name} ${user?.last_name}` &&
											"font-bold",
									)}
								>
									<p>
										{bid.timestamp?.toLocaleTimeString()}
										{"  "}
										{bid.name}
									</p>
									<p>+{bid.amount}s Bid</p>
								</li>
							))}
						</ol>
					</AccordionContent>
				</AccordionItem>
				<AccordionItem value="entries">
					<AccordionTrigger>
						<span>
							Entries&nbsp;
							<span className="font-bold">{battle.reservations.length}</span>
						</span>
					</AccordionTrigger>
					<AccordionContent>
						<ol className="list-none flex flex-col gap-4">
							{[...battle.reservations].reverse().map((resvervation, index) => {
								const padCount = String(battle.reservations.length).length;
								const id = battle.reservations.length - index;
								return (
									<li key={resvervation} className="flex gap-2">
										<p className="font-bold min-w-4">
											{String(id).padStart(padCount)}
										</p>
										<p>{resvervation}</p>
									</li>
								);
							})}
						</ol>
					</AccordionContent>
				</AccordionItem>
				<AccordionItem value="how-it-works">
					<AccordionTrigger>How it works</AccordionTrigger>
				</AccordionItem>
			</Accordion>
		</motion.div>
	);
}

function Timer({
	kind,
	value,
	className,
	variant = "default",
}: {
	kind: "battle" | "probation";
	value: string;
	className?: string;
	variant?: "default" | "sm";
}) {
	const letters = [...value];

	return (
		<motion.p
			layout
			className={cn(
				"font-bold text-center",
				variant === "default" && "text-4xl lg:text-7xl",
				variant === "sm" && "text-2xl lg:text-4xl",
				className,
			)}
			initial={{ translateY: "25%", opacity: 0 }}
			animate={{ translateY: 0, opacity: 1 }}
		>
			{letters.map((letter, index) => (
				<AnimatePresence key={`battle-timer-${kind}-${index}`} mode="popLayout">
					<motion.span
						key={`battle-timer-${kind}-${index}-${letter}`}
						layout
						layoutId={`battle-timer-${kind}-${index}-${letter}`}
						exit={{ translateY: "-100%", opacity: 0 }}
						initial={{ translateY: 0, opacity: 1 }}
						className="inline-block"
					>
						{letter}
					</motion.span>
				</AnimatePresence>
			))}
		</motion.p>
	);
}
